/**
 * HistoricoG2BPModal - Visualização do histórico de avaliações G2BP
 * Permite ver múltiplas avaliações do mesmo candidato ao longo do tempo
 * Inclui gráfico de evolução temporal dos percentuais D/I/S/C
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Minus,
  Calendar,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  LineChart as LineChartIcon,
  List
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine
} from 'recharts';

const CORES_DISC = {
  D: { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50', border: 'border-red-200', hex: '#dc2626' },
  I: { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-200', hex: '#f59e0b' },
  S: { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50', border: 'border-green-200', hex: '#22c55e' },
  C: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200', hex: '#3b82f6' }
};

const PERFIS_NOME = {
  D: 'Dominância',
  I: 'Influência',
  S: 'Estabilidade',
  C: 'Conformidade'
};

// Componente de mini gráfico de barras
function MiniBarChart({ percentages, height = 40 }) {
  return (
    <div className="flex gap-1 items-end" style={{ height }}>
      {['D', 'I', 'S', 'C'].map(trait => {
        const perc = percentages?.[trait] || 0;
        const barHeight = (perc / 100) * height;
        return (
          <div key={trait} className="flex-1 flex flex-col items-center">
            <div 
              className={`w-full ${CORES_DISC[trait].bg} rounded-t transition-all`}
              style={{ height: barHeight }}
            />
            <span className="text-[9px] font-medium mt-1">{trait}</span>
          </div>
        );
      })}
    </div>
  );
}

// Componente de comparação de tendência
function TendenciaIndicador({ atual, anterior }) {
  if (!anterior) return null;
  
  const diff = atual - anterior;
  if (Math.abs(diff) < 3) {
    return <Minus className="w-3 h-3 text-slate-400" />;
  }
  if (diff > 0) {
    return <TrendingUp className="w-3 h-3 text-green-500" />;
  }
  return <TrendingDown className="w-3 h-3 text-red-500" />;
}

// Tooltip customizado para o gráfico
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-semibold text-sm text-slate-700 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-bold" style={{ color: entry.color }}>
              {entry.value}%
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

// Formatter para a legenda do gráfico
const legendFormatter = (value) => PERFIS_NOME[value] || value;

// Componente principal do gráfico de evolução
function GraficoEvolucao({ dados, tipo = 'linha' }) {
  if (!dados || dados.length < 2) {
    return (
      <div className="h-64 flex items-center justify-center bg-slate-50 rounded-lg">
        <div className="text-center text-slate-500">
          <LineChartIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Necessário pelo menos 2 avaliações</p>
          <p className="text-xs">para visualizar a evolução temporal</p>
        </div>
      </div>
    );
  }

  // Preparar dados para o gráfico (ordem cronológica - mais antiga primeiro)
  const dadosGrafico = [...dados].reverse().map((aval, index) => {
    const percentuais = aval.percentuaisSintese || aval.percentages || {};
    const data = aval.created_at ? new Date(aval.created_at) : null;
    
    return {
      nome: data ? data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : `#${index + 1}`,
      dataCompleta: data ? data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
      D: percentuais.D || 0,
      I: percentuais.I || 0,
      S: percentuais.S || 0,
      C: percentuais.C || 0,
      perfil: aval.estilo || aval.dominant || '-'
    };
  });

  // Renderizar gráfico de área
  if (tipo === 'area') {
    return (
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={dadosGrafico} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="nome" 
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <YAxis 
              domain={[0, 100]} 
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#cbd5e1' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              formatter={legendFormatter}
            />
            <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="5 5" />
            <Area type="monotone" dataKey="D" name="D" stroke={CORES_DISC.D.hex} fill={CORES_DISC.D.hex} fillOpacity={0.2} strokeWidth={2} dot={{ fill: CORES_DISC.D.hex, strokeWidth: 2 }} />
            <Area type="monotone" dataKey="I" name="I" stroke={CORES_DISC.I.hex} fill={CORES_DISC.I.hex} fillOpacity={0.2} strokeWidth={2} dot={{ fill: CORES_DISC.I.hex, strokeWidth: 2 }} />
            <Area type="monotone" dataKey="S" name="S" stroke={CORES_DISC.S.hex} fill={CORES_DISC.S.hex} fillOpacity={0.2} strokeWidth={2} dot={{ fill: CORES_DISC.S.hex, strokeWidth: 2 }} />
            <Area type="monotone" dataKey="C" name="C" stroke={CORES_DISC.C.hex} fill={CORES_DISC.C.hex} fillOpacity={0.2} strokeWidth={2} dot={{ fill: CORES_DISC.C.hex, strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Renderizar gráfico de linha (padrão)
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={dadosGrafico} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="nome" 
            tick={{ fontSize: 11, fill: '#64748b' }}
            axisLine={{ stroke: '#cbd5e1' }}
          />
          <YAxis 
            domain={[0, 100]} 
            tick={{ fontSize: 11, fill: '#64748b' }}
            axisLine={{ stroke: '#cbd5e1' }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            formatter={legendFormatter}
          />
          <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="5 5" />
          <Line type="monotone" dataKey="D" name="D" stroke={CORES_DISC.D.hex} strokeWidth={3} dot={{ fill: CORES_DISC.D.hex, strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
          <Line type="monotone" dataKey="I" name="I" stroke={CORES_DISC.I.hex} strokeWidth={3} dot={{ fill: CORES_DISC.I.hex, strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
          <Line type="monotone" dataKey="S" name="S" stroke={CORES_DISC.S.hex} strokeWidth={3} dot={{ fill: CORES_DISC.S.hex, strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
          <Line type="monotone" dataKey="C" name="C" stroke={CORES_DISC.C.hex} strokeWidth={3} dot={{ fill: CORES_DISC.C.hex, strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Componente de análise de tendências
function AnaliseTendencias({ dados }) {
  if (!dados || dados.length < 2) return null;
  
  const primeiro = dados[dados.length - 1];
  const ultimo = dados[0];
  
  const calcularTendencia = (trait) => {
    const percPrimeiro = (primeiro.percentuaisSintese || primeiro.percentages)?.[trait] || 0;
    const percUltimo = (ultimo.percentuaisSintese || ultimo.percentages)?.[trait] || 0;
    return percUltimo - percPrimeiro;
  };
  
  const tendencias = ['D', 'I', 'S', 'C'].map(trait => ({
    trait,
    diff: calcularTendencia(trait),
    nome: PERFIS_NOME[trait]
  })).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  
  const maiorAumento = tendencias.find(t => t.diff > 0);
  const maiorReducao = tendencias.find(t => t.diff < 0);
  
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg p-4 border border-indigo-200">
      <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" />
        Análise de Tendências
      </h4>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        {maiorAumento && (
          <div className="bg-white rounded-lg p-3 border border-green-200">
            <p className="text-green-700 font-medium flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              Maior crescimento
            </p>
            <p className="text-lg font-bold text-green-800 mt-1">
              {maiorAumento.nome}
            </p>
            <p className="text-green-600 text-sm">
              +{maiorAumento.diff.toFixed(0)}% desde a primeira avaliação
            </p>
          </div>
        )}
        
        {maiorReducao && (
          <div className="bg-white rounded-lg p-3 border border-red-200">
            <p className="text-red-700 font-medium flex items-center gap-1">
              <TrendingDown className="w-4 h-4" />
              Maior redução
            </p>
            <p className="text-lg font-bold text-red-800 mt-1">
              {maiorReducao.nome}
            </p>
            <p className="text-red-600 text-sm">
              {maiorReducao.diff.toFixed(0)}% desde a primeira avaliação
            </p>
          </div>
        )}
      </div>
      
      <div className="mt-4 pt-3 border-t border-indigo-200">
        <p className="text-xs text-indigo-600 mb-2">Variação por fator:</p>
        <div className="flex gap-3">
          {['D', 'I', 'S', 'C'].map(trait => {
            const diff = calcularTendencia(trait);
            return (
              <div key={trait} className="flex items-center gap-1">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: CORES_DISC[trait].hex }}
                />
                <span className="text-xs font-medium">{trait}:</span>
                <span className={`text-xs font-bold ${
                  diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-slate-500'
                }`}>
                  {diff > 0 ? '+' : ''}{diff.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HistoricoG2BPModal({ 
  open, 
  onClose, 
  historico = [],
  nomeCandidata = ''
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [tipoGrafico, setTipoGrafico] = useState('linha');
  const [viewMode, setViewMode] = useState('grafico'); // 'grafico' ou 'lista'
  
  // Ordenar histórico por data (mais recente primeiro)
  const historicoOrdenado = [...historico].map(h => ({
    ...h.result,
    created_at: h.data || h.result?.created_at
  })).sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );
  
  const avaliacaoAtual = historicoOrdenado[selectedIndex];
  const avaliacaoAnterior = historicoOrdenado[selectedIndex + 1];
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Data não disponível';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (!historico || historico.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Histórico G2BP</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center text-slate-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Nenhuma avaliação G2BP encontrada no histórico.</p>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            Histórico G2BP - {nomeCandidata}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="grafico" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="grafico" className="flex items-center gap-2">
              <LineChartIcon className="w-4 h-4" />
              Gráfico de Evolução
            </TabsTrigger>
            <TabsTrigger value="lista" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Lista de Avaliações
            </TabsTrigger>
          </TabsList>
          
          {/* Aba do Gráfico de Evolução */}
          <TabsContent value="grafico" className="space-y-4">
            {/* Seletor de tipo de gráfico */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">
                {historicoOrdenado.length} avaliação(ões) encontrada(s)
              </p>
              <div className="flex gap-2">
                <Button
                  variant={tipoGrafico === 'linha' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoGrafico('linha')}
                >
                  Linhas
                </Button>
                <Button
                  variant={tipoGrafico === 'area' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoGrafico('area')}
                >
                  Área
                </Button>
              </div>
            </div>
            
            {/* Gráfico principal */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <LineChartIcon className="w-5 h-5 text-indigo-600" />
                  Evolução dos Fatores DISC ao Longo do Tempo
                </h3>
                <GraficoEvolucao dados={historicoOrdenado} tipo={tipoGrafico} />
              </CardContent>
            </Card>
            
            {/* Análise de tendências */}
            <AnaliseTendencias dados={historicoOrdenado} />
            
            {/* Legenda explicativa */}
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
              <p className="font-medium mb-2">Como interpretar o gráfico:</p>
              <ul className="space-y-1 text-xs">
                <li>• <span className="font-medium">Linha de 50%</span>: referência de equilíbrio (tracejada)</li>
                <li>• <span className="font-medium">Acima de 50%</span>: característica mais pronunciada</li>
                <li>• <span className="font-medium">Abaixo de 50%</span>: característica menos pronunciada</li>
                <li>• <span className="font-medium">Pontos</span>: cada avaliação realizada</li>
              </ul>
            </div>
          </TabsContent>
          
          {/* Aba da Lista de Avaliações */}
          <TabsContent value="lista" className="space-y-4">
            {/* Timeline de avaliações */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {historicoOrdenado.map((aval, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedIndex(idx)}
                  className={`
                    flex-shrink-0 px-3 py-2 rounded-lg border transition-all
                    ${idx === selectedIndex 
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700' 
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }
                  `}
                >
                  <div className="text-xs font-medium">
                    {idx === 0 ? 'Mais recente' : `#${historicoOrdenado.length - idx}`}
                  </div>
                  <div className="text-[10px] mt-1">
                    {formatDate(aval.created_at).split(',')[0]}
                  </div>
                </button>
              ))}
            </div>

            {/* Navegação */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIndex(prev => Math.min(prev + 1, historicoOrdenado.length - 1))}
                disabled={selectedIndex >= historicoOrdenado.length - 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
              <span className="text-sm text-slate-600">
                Avaliação {historicoOrdenado.length - selectedIndex} de {historicoOrdenado.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIndex(prev => Math.max(prev - 1, 0))}
                disabled={selectedIndex <= 0}
              >
                Próxima
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Avaliação selecionada */}
            {avaliacaoAtual && (
              <Card className="border-indigo-200">
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {formatDate(avaliacaoAtual.created_at)}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mt-2">
                        Perfil: {avaliacaoAtual.estilo || avaliacaoAtual.dominant}
                      </h3>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {avaliacaoAtual.versao && (
                        <Badge variant="outline" className="text-xs">
                          v{avaliacaoAtual.versao}
                        </Badge>
                      )}
                      {avaliacaoAtual.inconsistente && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Sob Análise
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Gráficos */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {/* Síntese */}
                    <div className="bg-indigo-50 rounded-lg p-4">
                      <p className="text-xs font-semibold text-indigo-700 mb-3">Perfil Composto</p>
                      <MiniBarChart 
                        percentages={avaliacaoAtual.percentuaisSintese || avaliacaoAtual.percentages} 
                        height={60}
                      />
                    </div>
                    
                    {/* MAIS */}
                    {avaliacaoAtual.percentuaisMais && (
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-green-700 mb-3">Perfil Público</p>
                        <MiniBarChart percentages={avaliacaoAtual.percentuaisMais} height={60} />
                      </div>
                    )}
                    
                    {/* MENOS */}
                    {avaliacaoAtual.percentuaisMenos && (
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-red-700 mb-3">Perfil Natural</p>
                        <MiniBarChart percentages={avaliacaoAtual.percentuaisMenos} height={60} />
                      </div>
                    )}
                  </div>

                  {/* Detalhes dos percentuais */}
                  <div className="grid grid-cols-4 gap-3">
                    {['D', 'I', 'S', 'C'].map(trait => {
                      const percAtual = (avaliacaoAtual.percentuaisSintese || avaliacaoAtual.percentages)?.[trait] || 0;
                      const percAnterior = avaliacaoAnterior 
                        ? (avaliacaoAnterior.percentuaisSintese || avaliacaoAnterior.percentages)?.[trait] 
                        : null;
                      
                      return (
                        <div 
                          key={trait}
                          className={`p-3 rounded-lg ${CORES_DISC[trait].light} ${CORES_DISC[trait].border} border`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`text-lg font-bold ${CORES_DISC[trait].text}`}>
                              {trait}
                            </span>
                            {percAnterior !== null && (
                              <TendenciaIndicador atual={percAtual} anterior={percAnterior} />
                            )}
                          </div>
                          <p className="text-2xl font-bold text-slate-800">{percAtual}%</p>
                          <p className="text-xs text-slate-500">{PERFIS_NOME[trait]}</p>
                          {percAnterior !== null && (
                            <p className="text-xs text-slate-400 mt-1">
                              Anterior: {percAnterior}%
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Comparação com avaliação anterior */}
                  {avaliacaoAnterior && (
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-semibold text-slate-700 mb-2">
                        Comparação com avaliação anterior
                      </p>
                      <p className="text-xs text-slate-500">
                        Anterior: {formatDate(avaliacaoAnterior.created_at)} - 
                        Perfil: {avaliacaoAnterior.estilo || avaliacaoAnterior.dominant}
                      </p>
                      <div className="flex gap-4 mt-3">
                        {['D', 'I', 'S', 'C'].map(trait => {
                          const atual = (avaliacaoAtual.percentuaisSintese || avaliacaoAtual.percentages)?.[trait] || 0;
                          const anterior = (avaliacaoAnterior.percentuaisSintese || avaliacaoAnterior.percentages)?.[trait] || 0;
                          const diff = atual - anterior;
                          
                          return (
                            <div key={trait} className="flex items-center gap-1">
                              <span className={`font-bold ${CORES_DISC[trait].text}`}>{trait}:</span>
                              <span className={`text-sm font-medium ${
                                diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-slate-500'
                              }`}>
                                {diff > 0 ? '+' : ''}{diff}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Resumo do histórico */}
        <div className="bg-slate-50 rounded-lg p-4 mt-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Resumo do Histórico</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Total</p>
              <p className="font-bold text-slate-800">{historicoOrdenado.length} avaliação(ões)</p>
            </div>
            <div>
              <p className="text-slate-500">Primeira</p>
              <p className="font-bold text-slate-800">
                {formatDate(historicoOrdenado[historicoOrdenado.length - 1]?.created_at).split(',')[0]}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Última</p>
              <p className="font-bold text-slate-800">
                {formatDate(historicoOrdenado[0]?.created_at).split(',')[0]}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Perfil mais frequente</p>
              <p className="font-bold text-slate-800">
                {(() => {
                  const perfis = historicoOrdenado.map(h => h.dominant);
                  const contagem = perfis.reduce((acc, p) => {
                    acc[p] = (acc[p] || 0) + 1;
                    return acc;
                  }, {});
                  const maisFrequente = Object.entries(contagem).sort(([,a], [,b]) => b - a)[0];
                  return maisFrequente ? `${maisFrequente[0]} (${maisFrequente[1]}x)` : '-';
                })()}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
